/**
 * Her lease page's questions to the intake service (MTR #578, step C). The link's token is the only
 * credential; nothing is stored in the browser.
 *
 * Each call resolves to the server's answer — `status: 'ok'` with the lease, or a refusal with `says`
 * — and throws only when no answer came back, in words she can act on.
 */
export function createRenterLeaseApi({ baseUrl, fetchImpl = null }) {
  const base = String(baseUrl ?? '').replace(/\/+$/, '')
  const call = async (path, body) => {
    let res
    try {
      res = await (fetchImpl ?? fetch)(`${base}/public/lease/${path}`, body === undefined
        ? { method: 'GET' }
        : { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
    } catch {
      throw new Error('We couldn’t reach Stayoo. Check your connection and try again.')
    }
    const out = await res.json().catch(() => null)
    if (res.status === 404) return { status: 'not_found' }
    if (res.status === 429) return { status: 'too_many_requests', says: 'Too many tries in a short time. Please wait a few minutes, then try again.' }
    return out ?? { status: 'error', says: 'Something went wrong on our side. Please try again in a minute.' }
  }
  return {
    get: (token) => call(token),
    /* With the version of the words she read, so a change in between is caught (review of #578 C). */
    consent: (token, version) => call(`${token}/consent`, { agree: true, version }),
    initial: (token, initials) => call(`${token}/initial`, { initials }),
    sign: (token, { kind, signature }) => call(`${token}/sign`, { kind, signature }),
    /* "Open the full lease" (#746): a link, not a call — the service checks her link, records that it
       served her the lease, and redirects to a freshly minted address of the PDF. */
    documentHref: (token) => `${base}/public/lease/${token}/document`,
  }
}
